/**
 * useModalKeys — keyboard dismiss/submit for a BESPOKE modal that rolls its own
 * overlay markup instead of using the shared <AppDialog> (CLAUDE.md standing rule
 * 14, ADR-0013).
 *
 * <AppDialog> already gives Esc / Enter / Ctrl+S; this brings the same to the older
 * hand-rolled popups WITHOUT converting them to `aria-modal` — deliberately, because
 * these popups host toolbars whose shortcuts flow through the global hotkey store,
 * and the store yields entirely to an `[aria-modal]` (so flipping them would silence
 * those shortcuts). Instead the handler lives on the popup's OWN root element:
 *
 *   const modalKeys = useModalKeys(open, { onClose, onSubmit });
 *   return <div className="acc-dialog" {...modalKeys}> … </div>;
 *
 * Why this is safe (same mechanics <AppDialog> and the store already use):
 *  • It is a React `onKeyDown` on the popup root, so it fires AFTER any deeper
 *    handler in the popup (a combo / sub-picker) — and it bails when that inner
 *    handler already consumed the key (`e.defaultPrevented`). So Esc closes the
 *    INNERMOST open thing first, never the wrong layer.
 *  • It runs BEFORE the global store's `document` keydown listener, and calls
 *    `preventDefault`, so the store bails (`hotkeyStore` line 163) — the form BEHIND
 *    the popup never sees the Esc, so its Cancel/Back can't fire and discard edits.
 *  • It auto-focuses the popup root on open (only when focus isn't already inside),
 *    so the keys reach the handler even for popups that don't focus a field.
 *  • Enter submits only when focus is NOT in a textarea / select / button / link /
 *    contenteditable — so it never fires mid-typing.
 */
import { useCallback, useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { focusFirstControl } from './useAutoFocusFirstControl';

export interface ModalKeysOptions {
  /** Esc (and a backdrop ✕) closes the popup. */
  onClose?: () => void;
  /** Enter (outside a text field) and Ctrl/⌘+S activate the primary/save action. */
  onSubmit?: () => void;
}

export interface ModalKeysBinding {
  ref: (el: HTMLElement | null) => void;
  onKeyDown: (e: ReactKeyboardEvent) => void;
  tabIndex: number;
}

export function useModalKeys(open: boolean, opts: ModalKeysOptions): ModalKeysBinding {
  const { onClose, onSubmit } = opts;
  const rootRef = useRef<HTMLElement | null>(null);

  // On open, focus the popup's FIRST usable control (focus-first-control
  // standard, ADR-0013) — else the root, so its onKeyDown still receives keys.
  // Never steal focus from a control the popup already focused inside itself.
  // (Was root-only: the New Client / New Contact forms opened with nothing to
  // type into — browser walkthrough 2026-10-20.)
  useEffect(() => {
    if (!open) return;
    const el = rootRef.current;
    if (!el) return;
    // Skip only when a control INSIDE already has focus — the root itself does
    // not count (React's dev double effect focuses it on the first pass, and
    // the second pass must still look for a field).
    const active = document.activeElement;
    if (active && active !== el && el.contains(active)) return;
    // Buttons excluded: a popup with no field would otherwise land on its
    // header's Maximize / Close.
    if (focusFirstControl(el, { includeButtons: false })) return;
    try { el.focus({ preventScroll: true }); } catch { /* not focusable in some envs */ }
    // A popup that loads its record first shows no field yet. Wait (briefly) for
    // the first one — but only while focus is still parked on the root, so a
    // user who has already clicked somewhere keeps their place.
    const observer = new MutationObserver(() => {
      if (document.activeElement !== el) { observer.disconnect(); return; }
      if (focusFirstControl(el, { includeButtons: false })) observer.disconnect();
    });
    observer.observe(el, { childList: true, subtree: true });
    const stop = window.setTimeout(() => observer.disconnect(), 5000);
    return () => { observer.disconnect(); window.clearTimeout(stop); };
  }, [open]);

  const ref = useCallback((el: HTMLElement | null) => { rootRef.current = el; }, []);

  const onKeyDown = useCallback((e: ReactKeyboardEvent) => {
    if (e.defaultPrevented || e.nativeEvent.isComposing) return;   // a nested popup/combo already handled it (or IME composing)
    const key = e.key;

    if (key === 'Escape' && onClose && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault(); e.stopPropagation();
      onClose();
      return;
    }
    if (!onSubmit) return;

    // Ctrl/⌘+S — always submits (blocks the browser Save-page dialog too).
    if ((key === 's' || key === 'S') && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
      e.preventDefault(); e.stopPropagation();
      onSubmit();
      return;
    }
    // Enter — submit only when NOT typing in a field / on a button or link.
    if (key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON' || tag === 'A') return;
      if (t?.isContentEditable) return;
      e.preventDefault(); e.stopPropagation();
      onSubmit();
    }
  }, [onClose, onSubmit]);

  return { ref, onKeyDown, tabIndex: -1 };
}
